import { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { srsMapping, levelMapping } from '../../utils/mappings';
import { BookOpenIcon, ChatBubbleLeftRightIcon, StarIcon, ChartPieIcon, SpeakerWaveIcon } from '@heroicons/react/24/solid';
import GlassyIcon from '../common/GlassyIcon';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { useMetadata } from '../../contexts/MetadataProvider';
import { formatListeningTime } from '../../utils/listeningStats';

const ProgressHeader = ({ contentType }) => {
  // Get real-time stats from global context
  const { stats, loading } = useDashboardStats();
  const { listening } = useMetadata();
  const listeningTime = formatListeningTime(listening?.[contentType]?.seconds || 0);
  const avgListensRatio = listening?.[contentType]?.avgListens ?? null;

  // Extract stats for the specific content type from global context
  const summaryStats = useMemo(() => {
    if (!stats) return null;
    
    const relevantStats = contentType === 'words' ? stats.wordsStats : stats.sentencesStats;
    
    // Count only actual skipped items (not including stage 7)
    const skipped = relevantStats.bySrsStage.skipped || 0;
    
    // Combine skipped + stage 7 for distribution display
    const combinedSrsStage = { ...relevantStats.bySrsStage };
    if (combinedSrsStage.skipped) {
      combinedSrsStage[7] = (combinedSrsStage[7] || 0) + combinedSrsStage.skipped;
      delete combinedSrsStage.skipped;
    }
    
    return {
      totalLearned: relevantStats.totalLearned || 0,
      skipped,
      bySrsStage: combinedSrsStage,
      byLevel: relevantStats.byLevel || {},
      accuracy: relevantStats.accuracy || { correct: 0, incorrect: 0, ratio: 0 }
    };
  }, [stats, contentType]);
  
  const isStatsLoading = loading;
  // Get theme configuration for the header
  const getHeaderConfig = () => {
    if (contentType === 'words') {
      return {
        icon: BookOpenIcon,
        title: 'My Words',
        subtitle: 'Track your vocabulary progress',
        gradientFrom: 'var(--color-words)',
        gradientTo: 'var(--color-words-accent)',
      };
    } else {
      return {
        icon: ChatBubbleLeftRightIcon,
        title: 'My Sentences',
        subtitle: 'Review your sentence mastery',
        gradientFrom: 'var(--color-sentences)',
        gradientTo: 'var(--color-sentences-accent)',
      };
    }
  };

  // Compact distribution bar component
  const DistributionBar = ({ stats, total, colorPrefix, mapping }) => (
    <div className="w-full flex h-1.5 rounded-full overflow-hidden bg-border">
      {Object.entries(stats).map(([key, count]) => {
        if (count === 0) return null;
        const percentage = (count / total) * 100;
        return (
          <div
            key={key}
            className="h-full transition-all duration-300"
            style={{
              width: `${percentage}%`,
              backgroundColor: `var(--color-${colorPrefix}-${key})`,
            }}
            title={`${mapping[key]}: ${count.toLocaleString()}`}
          />
        );
      })}
    </div>
  );

  const headerConfig = getHeaderConfig();
  const HeaderIcon = headerConfig.icon;

  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  return (
    <div className="relative mb-8 p-6 rounded-2xl border-2 border-border overflow-hidden animate-fade-in bg-background-secondary/80">
      <div className="relative z-10 text-content-primary">
        {/* Top row - Unchanged */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <GlassyIcon 
              icon={HeaderIcon} 
              size="lg" 
              customGradient={contentType === 'words' 
                ? 'linear-gradient(135deg, var(--color-words), var(--color-words-accent))'
                : 'linear-gradient(135deg, var(--color-sentences), var(--color-sentences-accent))'
              }
              customShadow={contentType === 'words'
                ? '0 4px 6px -1px var(--color-words-shadow), 0 2px 4px -1px var(--color-words-shadow)'
                : '0 4px 6px -1px var(--color-sentences-shadow), 0 2px 4px -1px var(--color-sentences-shadow)'
              }
              className="w-12 h-12"
            />
            <div>
              <h1 className="text-2xl lg:text-3xl font-black leading-tight">{headerConfig.title}</h1>
              <p className="text-sm font-medium text-content-secondary">{headerConfig.subtitle}</p>
            </div>
          </div>
          <div className="flex flex-shrink-0 text-center">
            {isStatsLoading ? (
              <Skeleton width={70} height={40} />
            ) : (
              summaryStats && (
                <div>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold">{formatNumber(summaryStats.totalLearned)}</div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-content-secondary">Learned</div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Bottom row - Skeletons or Stats */}
        {isStatsLoading ? (
          <>
            {/* Skeletons - Unchanged */}
            <div className="hidden md:grid md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-background-primary rounded-xl p-4 border border-border h-[128px] flex flex-col justify-center">
                  <Skeleton height={20} width="60%" className="mb-3" />
                  <Skeleton height={30} width="80%" />
                </div>
              ))}
            </div>
            <div className="flex md:hidden justify-center gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-background-primary rounded-xl p-4 border border-border w-24 h-28 flex flex-col justify-center">
                  <Skeleton height={25} />
                  <Skeleton height={15} className="mt-2" />
                </div>
              ))}
            </div>
          </>
        ) : summaryStats ? (
          <>
            {/* Desktop Stats - Unchanged */}
            <div className="hidden md:grid md:grid-cols-3 gap-4">
              {/* Progress Overview */}
              <div className="bg-background-primary rounded-xl p-4 border border-border shadow-sm flex flex-col justify-between h-[128px]">
                <div className="flex items-center gap-2 self-center">
                  <SpeakerWaveIcon className="w-4 h-4 text-content-secondary" />
                  <span className="text-sm font-semibold text-content-primary">Listening</span>
                </div>
                <div className="text-center w-full">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-content-secondary">Listened</span>
                      <span className="text-xl font-bold text-content-primary">{listeningTime}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-content-secondary">Listens/Review</span>
                      <span className="text-xl font-bold text-content-primary">{avgListensRatio != null ? avgListensRatio.toFixed(1) : '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribution */}
              <div className="bg-background-primary rounded-xl p-4 border border-border shadow-sm flex flex-col justify-between h-[128px]">
                <div className="flex items-center gap-2 self-center">
                  <ChartPieIcon className="w-4 h-4 text-content-secondary" />
                  <span className="text-sm font-semibold text-content-primary">Distribution</span>
                </div>
                <div className="text-center w-full">
                  {summaryStats.totalLearned > 0 ? (
                    <div className="space-y-2">
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-content-secondary">SRS Stages</span>
                        </div>
                        <DistributionBar stats={summaryStats.bySrsStage} total={summaryStats.totalLearned} colorPrefix="srs" mapping={srsMapping} />
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-content-secondary">N-Levels</span>
                        </div>
                        <DistributionBar stats={summaryStats.byLevel} total={summaryStats.totalLearned} colorPrefix="n" mapping={levelMapping} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-content-secondary py-4">No data yet</p>
                  )}
                </div>
              </div>

              {/* Review Accuracy */}
              <div className="bg-background-primary rounded-xl p-4 border border-border shadow-sm flex flex-col justify-between h-[128px]">
                <div className="flex items-center gap-2 self-center">
                  <StarIcon className="w-4 h-4 text-content-secondary" />
                  <span className="text-sm font-semibold text-content-primary">Review Accuracy</span>
                </div>
                <div className="text-center w-full">
                  {summaryStats.accuracy && summaryStats.accuracy.total > 0 ? (
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-content-primary">{(summaryStats.accuracy.ratio * 100).toFixed(0)}%</div>
                      <div className="w-full flex h-1.5 rounded-full overflow-hidden bg-border">
                        <div className="h-full bg-green-500" style={{ width: `${summaryStats.accuracy.ratio * 100}%` }} />
                        <div className="h-full bg-red-500" style={{ width: `${100 - summaryStats.accuracy.ratio * 100}%` }} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-content-secondary py-4">No reviews yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* MODIFIED: Real stats data for Mobile */}
            <div className="grid md:hidden grid-cols-3 gap-3">
                {/* Listening */}
                <div className="bg-background-primary h-28 rounded-xl p-2 pb-3 border border-border shadow-sm text-center flex flex-col">
                  <div className="flex-grow flex flex-col justify-evenly items-center">
                    <div>
                      <div className="text-base font-bold text-content-primary">{listeningTime}</div>
                      <div className="text-[11px] font-semibold leading-tight text-content-secondary">Listened</div>
                    </div>
                    <div>
                      <div className="text-base font-bold text-content-primary">{avgListensRatio != null ? avgListensRatio.toFixed(1) : '—'}</div>
                      <div className="text-[11px] font-semibold leading-tight text-content-secondary">Listens/Review</div>
                    </div>
                  </div>
                </div>

                {/* Distribution */}
                <div className="bg-background-primary h-28 rounded-xl p-2 border border-border shadow-sm text-center flex flex-col">
                  <div className="flex-grow flex flex-col justify-evenly items-center">
                    {summaryStats.totalLearned > 0 ? (
                      <>
                        <div className="w-full">
                          <div className="text-xs font-semibold text-content-secondary mb-1">SRS</div>
                          <DistributionBar stats={summaryStats.bySrsStage} total={summaryStats.totalLearned} colorPrefix="srs" mapping={srsMapping} />
                        </div>
                        <div className="w-full">
                          <div className="text-xs font-semibold text-content-secondary mb-1">Level</div>
                           <DistributionBar stats={summaryStats.byLevel} total={summaryStats.totalLearned} colorPrefix="n" mapping={levelMapping} />
                        </div>
                      </>
                    ) : (
                      <>
                        <ChartPieIcon className="h-6 w-6 text-content-secondary" />
                        <span className="text-xs text-content-secondary">No Data</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Review Accuracy */}
                <div className="bg-background-primary h-28 rounded-xl p-2 border border-border shadow-sm text-center flex flex-col">
                   <div className="flex-grow flex flex-col justify-evenly items-center">
                    {summaryStats.accuracy && summaryStats.accuracy.total > 0 ? (
                      <div className='w-full'>
                        <div className="text-lg font-bold text-content-primary">{(summaryStats.accuracy.ratio * 100).toFixed(0)}%</div>
                        <div className="text-xs font-semibold text-content-secondary leading-tight">
                          Review Accuracy
                        </div>
                        <div className="w-full mt-1">
                          <div className="w-full flex h-1.5 rounded-full overflow-hidden bg-border">
                            <div className="h-full bg-green-500" style={{ width: `${summaryStats.accuracy.ratio * 100}%` }} />
                            <div className="h-full bg-red-500" style={{ width: `${100 - summaryStats.accuracy.ratio * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <StarIcon className="h-6 w-6 text-content-secondary" />
                        <div className="text-xs text-content-secondary leading-tight">
                          No Reviews
                        </div>
                      </>
                    )}
                  </div>
                </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ProgressHeader;